import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import Checkbox from "@repo/ui/components:ats/ui/checkbox";
import { Badge } from "@repo/ui/components:ats/ui/badge";
import { ScrollArea } from "@repo/ui/components:ats/ui/scroll-area";
import { cn } from "@repo/ui/lib/utils";

type SelectMode = "single" | "multiple";
type SelectValue<T extends SelectMode> = T extends "multiple"
  ? string[]
  : string;
export type SelectOnChange<T extends SelectMode> = T extends "multiple"
  ? (value: string[]) => void
  : (value: string) => void;

export interface SelectDropdownProps<T extends SelectMode>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  mode: T;
  options: { label: string; value: string }[] | string[];
  value: SelectValue<T>;
  onChange: SelectOnChange<T>;
  checkStyle?: "check" | "checkbox" | "badge" | "none";
  badgeText?: string;
  badgeProps?: {
    variant?: "filled" | "outlined";
    state?: "primary" | "secondary" | "error";
    size?: "md" | "lg";
    rounded?: "circle" | "square";
  };
  isLoading?: boolean;
  onClose?: () => void;
  hasOverflow?: boolean;
  primaryOption?: {
    label: string;
    onClick: () => void;
    showBorder?: boolean;
  };
  errorOption?: {
    label: string;
    onClick: () => void;
    showBorder?: boolean;
  };
  disabledOptions?: string[];
  readonly?: boolean;
  plainReadOnly?: boolean;
}

const SelectDropdownList = <T extends SelectMode>({
  className,
  mode,
  options,
  value,
  onChange,
  checkStyle = "check",
  badgeText,
  badgeProps,
  isLoading,
  onClose,
  hasOverflow = false,
  primaryOption,
  errorOption,
  disabledOptions = [],
  readonly = false,
  plainReadOnly = false,
  ...props
}: SelectDropdownProps<T>) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, []);

  const handleSelect = (clickedValue: string) => {
    if (readonly || plainReadOnly) return;

    if (mode === "multiple") {
      const currentValue = value as string[];
      const newSelected = currentValue.includes(clickedValue)
        ? currentValue.filter((item) => item !== clickedValue)
        : [...currentValue, clickedValue];
      (onChange as (value: string[]) => void)(newSelected);
    } else {
      (onChange as (value: string) => void)(clickedValue);
      onClose?.();
    }
  };

  const isItemSelected = (option: string) => {
    if (mode === "multiple") {
      return (value as string[]).includes(option);
    }
    return option === value;
  };

  const isInteractive = !(readonly || plainReadOnly);

  return (
    <ScrollArea
      className={cn("relative overflow-y-auto w-full", hasOverflow && "h-60")}
      onWheel={(e) => {
        e.stopPropagation();
        const scrollContainer = e.currentTarget;
        scrollContainer.scrollTop += e.deltaY;
      }}
    >
      <div ref={listRef} className={cn("w-full", className)} {...props}>
        {isLoading ? (
          <div className="flex w-full items-center justify-center">
            <Loader2 size={16} className="animate-spin" />
          </div>
        ) : options.length === 0 ? (
          <div className="flex w-full items-center justify-center text-sm text-semantic-secondary-foreground-normal-default">
            데이터가 없습니다.
          </div>
        ) : (
          <>
            {options.map((option) => {
              const optionValue =
                typeof option === "string" ? option : option.value;
              const label = typeof option === "string" ? option : option.label;
              const isSelected = isItemSelected(optionValue);
              const isDisabled =
                disabledOptions.includes(optionValue) ||
                readonly ||
                plainReadOnly;

              return (
                <button
                  key={optionValue}
                  ref={isSelected ? selectedItemRef : null}
                  type="button"
                  className={cn(
                    "relative flex w-full select-none items-center rounded px-[10px] py-1.5 text-sm outline-none text-left",
                    isInteractive && !disabledOptions.includes(optionValue)
                      ? "cursor-pointer hover:bg-semantic-secondary-background-normal-hovered"
                      : "cursor-default",
                    (disabledOptions.includes(optionValue) || !isInteractive) &&
                      "opacity-50 pointer-events-none"
                  )}
                  onClick={() => !isDisabled && handleSelect(optionValue)}
                  disabled={isDisabled}
                >
                  <div className="flex w-full items-center gap-2">
                    {checkStyle === "none" ? null : checkStyle === "badge" ? (
                      isSelected && (
                        <Badge {...badgeProps} className="absolute right-2">
                          {badgeText}
                        </Badge>
                      )
                    ) : (
                      <Checkbox
                        id={optionValue}
                        checked={isSelected}
                        variant={checkStyle}
                      />
                    )}
                    <span className="w-full">{label}</span>
                  </div>
                </button>
              );
            })}
            {primaryOption && isInteractive && (
              <button
                type="button"
                className={cn(
                  "relative flex w-full select-none items-center px-[10px] py-1.5 text-sm outline-none text-left text-semantic-primary-foreground-global-default",
                  primaryOption.showBorder !== false &&
                    "border-t border-semantic-secondary-border-normal-default",
                  isInteractive
                    ? "cursor-pointer hover:bg-semantic-secondary-background-normal-hovered hover:rounded"
                    : "cursor-default"
                )}
                onClick={isInteractive ? primaryOption.onClick : undefined}
                disabled={!isInteractive}
              >
                <span className="w-full">{primaryOption.label}</span>
              </button>
            )}
            {errorOption && isInteractive && (
              <button
                type="button"
                className={cn(
                  "relative flex w-full select-none items-center px-[10px] py-1.5 text-sm outline-none text-left text-semantic-error-global-global-default",
                  errorOption.showBorder !== false &&
                    "border-t border-semantic-secondary-border-normal-default",
                  isInteractive
                    ? "cursor-pointer hover:bg-semantic-secondary-background-normal-hovered hover:rounded"
                    : "cursor-default"
                )}
                onClick={isInteractive ? errorOption.onClick : undefined}
                disabled={!isInteractive}
              >
                <span className="w-full">{errorOption.label}</span>
              </button>
            )}
          </>
        )}
      </div>
    </ScrollArea>
  );
};

SelectDropdownList.displayName = "SelectDropdownList";

export default SelectDropdownList;
